import { Order } from '@features/siteStatusV2/types';
import { isFtthOrder } from '@features/siteStatusV2/utils';
import theme from '@theme';
import React, { Dispatch, useMemo } from 'react';
import { BoxFill, Clock, RssFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { StatusBadge } from '../status-badge';
import CategoryBadge from './CategoryBadge';
import { convertDateFormat } from '@features/siteStatusV2/utils/helpers';

import { TranslationNamespaces } from '../../../../locales/constants/translationNamespaces.enum';

interface OrderElementProps {
  order: Order;
  orderSelected: Order | undefined;
  setOrderSelected: Dispatch<React.SetStateAction<Order | undefined>>;
}

const OrderElement: React.FC<OrderElementProps> = ({ order, orderSelected, setOrderSelected }) => {
  const { t } = useTranslation(TranslationNamespaces.SITE_STATUS);

  const isElementActive = useMemo(() => order.id === orderSelected?.id, [order, orderSelected]);

  const selectOrder = () => {
    if (!isElementActive) {
      setOrderSelected(order);
    }
  };

  return (
    <OrderCardContainer
      isElementActive={isElementActive}
      className="mb-2"
      onClick={selectOrder}
      type="button"
    >
      <div className="d-flex align-items-center justify-content-between mb-2">
        <div className="d-flex align-items-center">
          <OrderTag className="d-flex align-items-center">
            <BoxFill fill={theme.palette.blue[500]} width={9} height={9} />
            <OrderTagText> {t('OrderList.order', `Order`)}</OrderTagText>
          </OrderTag>
          <CategoryBadge category={order.category} />
          <OrderName>
            {isFtthOrder(order) && order.referenceServiceTaken
              ? order.referenceServiceTaken
              : order.name}
          </OrderName>
        </div>
        <div className="d-flex align-items-center">
          <StatusBadge order={order} />
          <Separator />
          <CustomRssIcon />
        </div>
      </div>
      <div className="d-flex align-items-center justify-content-between">
        <div className="d-flex items-center">
          <Clock fill={theme.palette.grey[400]} width={11} height={11} />
          <OrderFooterText>
            {t('OrderList.orderDate', `Order Date`)}
            {' : '}
            {convertDateFormat(order.createdDate)}
          </OrderFooterText>
        </div>
        <OrderFooterText>{order.commercialOperator}</OrderFooterText>
      </div>
    </OrderCardContainer>
  );
};

const SharedBeforeStyles = `
  content: "";
  display: inline-block;
  position: absolute;
  left: -0.565rem;
  top: 50%;
  transform: translateY(-50%);
  width: 0.25rem;
  height: 0.25rem;
  background: ${theme.palette.grey[300]};
  border-radius: 50%;
`;

const Separator = styled.div`
  width: 0.25rem;
  height: 0.25rem;
  background: ${theme.palette.grey[300]};
  border-radius: 50%;
  margin-right: 0.375rem;
  margin-left: 0.375rem;
`;

interface OrderCardContainerProps {
  isElementActive: boolean;
}

const OrderCardContainer = styled.button<OrderCardContainerProps>`
  position: relative;
  width: 100%;
  border-radius: 0.25rem;
  padding: 0.7rem 1.125rem 0.7rem 1.125rem;
  ${({ isElementActive }) =>
    isElementActive
      ? `
      border: 1px solid ${theme.palette.blue[500]};
      background-color: ${theme.palette.blue[50]};
      &:hover {
        cursor: default;
      }
      `
      : `
    border: 1px solid ${theme.palette.grey[200]};
    background-color: white;
    &:hover {
      border: 1px solid ${theme.palette.blue[500]};
    }
    `}
`;

const OrderFooterText = styled.div`
  font-size: 0.5625rem;
  font-weight: 500;
  line-height: 0.75rem;
  color: ${theme.palette.grey[400]};
  margin-left: 0.25rem;
`;

const OrderTag = styled.div`
  font-size: 0.625rem;
  font-weight: 500;
  line-height: 0.75rem;
  color: ${theme.palette.blue[500]};
  padding: 0.1875rem 0.25rem 0.1875rem 0.5rem;
  border-radius: 0.25rem 0 0 0;
  background-color: ${theme.palette.blue[100]};
`;

const OrderTagText = styled.span`
  margin-left: 0.25rem;
`;
const OrderName = styled.span`
  position: relative;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1rem;
  color: ${theme.palette.grey[500]};
  margin-left: 0.9375rem;

  &::before {
    ${SharedBeforeStyles};
  }
`;

const CustomRssIcon = styled(RssFill)`
  width: 0.875rem;
  fill: ${theme.palette.orange[500]};
`;

export default OrderElement;
